import { t } from "../i18n";
// Parsing and validation of typed/pasted money amounts. Pure functions (no React) so any form can use them.
// Amounts follow the app's unit (thousands, like wages); the database keeps at most two decimals.
export const MAX_AMOUNT = 99999999.99;
const DEFAULT_DECIMALS = 2;

const ARABIC_DIGITS = "٠١٢٣٤٥٦٧٨٩";
const PERSIAN_DIGITS = "۰۱۲۳۴۵۶۷۸۹";

// Digits typed on Arabic keyboards, Arabic separators, spaces and invisible bidi marks -> plain ASCII.
export function normalizeAmountText(raw) {
  return String(raw ?? "")
    .replace(/[٠-٩]/g, (d) => String(ARABIC_DIGITS.indexOf(d)))
    .replace(/[۰-۹]/g, (d) => String(PERSIAN_DIGITS.indexOf(d)))
    .replace(/٫/g, ".") // Arabic decimal separator
    .replace(/[٬،]/g, ",") // Arabic thousands separator and comma
    .replace(/−/g, "-") // minus sign
    .replace(/[\s ‎‏‪-‮⁦-⁩]/g, "")
    .trim();
}

export const AMOUNT_ERRORS = {
  empty: t("أدخل مبلغ الإكرامية (اكتب 0 إذا لا توجد إكرامية)."),
  invalid: t("المبلغ غير صالح. استخدم أرقاماً فقط، مثال: 25 أو 12.5"),
  negative: t("لا يمكن أن يكون المبلغ سالباً."),
  decimals: t("الحد الأقصى خانتان عشريتان بعد الفاصلة."),
  wholeOnly: t("هذه العملة لا تقبل كسوراً: اكتب رقماً صحيحاً."),
  tooLarge: t("المبلغ كبير جداً (الحد الأقصى 99,999,999.99)."),
};

// -> { ok: true, value, text } | { ok: false, reason: keyof AMOUNT_ERRORS }
// An empty field is its own case: it is never treated as zero. "0" is a valid amount.
// `decimals` is how many places are allowed (0 = whole numbers only, e.g. Iraqi dinars).
export function parseAmount(raw, max = MAX_AMOUNT, decimals = DEFAULT_DECIMALS) {
  let text = normalizeAmountText(raw);
  if (text === "") return { ok: false, reason: "empty" };
  if (text.startsWith("-")) return { ok: false, reason: "negative" };

  // Thousands separators are accepted only in their proper place (1,000 or 1,234,567.5).
  if (text.includes(",")) {
    if (!/^\d{1,3}(,\d{3})+(\.\d+)?$/.test(text)) return { ok: false, reason: "invalid" };
    text = text.replace(/,/g, "");
  }
  if (!/^(\d+(\.\d+)?|\.\d+)$/.test(text)) return { ok: false, reason: "invalid" };
  if (text.startsWith(".")) text = `0${text}`;

  if ((text.split(".")[1] || "").length > decimals)
    return { ok: false, reason: decimals === 0 ? "wholeOnly" : "decimals" };
  const value = Number(text);
  if (!Number.isFinite(value) || value > max) return { ok: false, reason: "tooLarge" };
  return { ok: true, value, text };
}

// Backend validation text, when the server rejects what the client accepted.
export function serverAmountError(err) {
  return err?.response?.data?.errors?.tip_amount?.[0] || null;
}
